import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
// Components
import JunoPage from "../../components/JunoPage/JunoPage";
import OrganizationChartView from "../../components/OrganizationChartView/OrganizationChartView";
import OrganizationTreeView from "../../components/OrganizationTreeView/OrganizationTreeView";
// Resources
import { Organization } from "fhir/r5";
// HL7 Front Library
import { Loader, Title } from "@fyrstain/hl7-front-library";
// React Bootstrap
import { Badge, Button, Card, Form, InputGroup } from "react-bootstrap";
// Translation
import i18n from "i18next";
// Services
import OrganizationService from "../../services/OrganizationService";
// Utilities
import {
  buildOrganizationTree,
  countOrganizationLeaves,
  countOrganizationNodes,
  countOrganizationsByLevel,
  filterOrganizationTree,
  normalizeSearchValue,
} from "../../utils/organizationTree";
// Styles
import "../../components/HierarchyView/HierarchyView.css";

const OrganizationStructure: FunctionComponent = () => {
  //////////////////////////////
  //        Navigation        //
  //////////////////////////////

  const navigate = useNavigate();

  //////////////////////////////
  //           State          //
  //////////////////////////////

  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [collapsedNodes, setCollapsedNodes] = useState<Record<string, boolean>>(
    {}
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"tree" | "chart">("tree");

  const onError = useCallback(() => {
    navigate("/Error");
  }, [navigate]);

  //////////////////////////////
  //      Data loading        //
  //////////////////////////////

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await OrganizationService.loadAllInternalOrganizations();
      setOrganizations(response);
    } catch (error) {
      onError();
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  //////////////////////////////
  //     Tree data            //
  //////////////////////////////

  const tree = useMemo(() => buildOrganizationTree(organizations), [organizations]);
  const normalizedSearchTerm = useMemo(
    () => normalizeSearchValue(searchTerm.trim()),
    [searchTerm]
  );
  const filteredTree = useMemo(
    () => filterOrganizationTree(tree, normalizedSearchTerm),
    [tree, normalizedSearchTerm]
  );
  const totalOrganizations = organizations.length;
  const totalRoots = filteredTree.nodes.length;
  const totalLeaves = countOrganizationLeaves(filteredTree.nodes);
  const treeCount = countOrganizationNodes(filteredTree.nodes);
  const organizationsByLevel = useMemo(
    () => countOrganizationsByLevel(filteredTree.nodes),
    [filteredTree.nodes]
  );
  const hasSearch = normalizedSearchTerm.length > 0;
  const visibleMatches = hasSearch ? filteredTree.matches : totalOrganizations;
  const isChartView = viewMode === "chart";

  //////////////////////////////
  //          Actions         //
  //////////////////////////////

  const toggleNode = useCallback((key: string) => {
    setCollapsedNodes((current) => ({
      ...current,
      [key]: !(current[key] ?? true),
    }));
  }, []);

  const goToOrganizationDetails = useCallback(
    (organizationId?: string | null) => {
      if (organizationId) {
        navigate(`/Organization/${encodeURIComponent(organizationId)}`);
      }
    },
    [navigate]
  );

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <JunoPage
      titleKey={i18n.t("title.organizationstructure")}
      loading={loading && !isChartView}
      needsLogin={true}
    >
      <div className="section">
        <Card className="card">
          <Card.Header>
            <Title
              level={2}
              prefix="Structure"
              content={i18n.t("title.organizationstructure")}
            />
          </Card.Header>
          <Card.Body>
            <div className="hierarchy-view-switch">
              <Button
                variant={isChartView ? "outline-secondary" : "secondary"}
                onClick={() => setViewMode("tree")}
              >
                {i18n.t("label.treeview")}
              </Button>
              <Button
                variant={isChartView ? "secondary" : "outline-secondary"}
                onClick={() => setViewMode("chart")}
              >
                {i18n.t("label.chartview")}
              </Button>
            </div>

            {isChartView ? (
              <OrganizationChartView
                organizations={organizations}
                loading={loading}
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
              />
            ) : (
              <>
                <div className="hierarchy-search">
                  <InputGroup>
                    <Form.Control
                      type="search"
                      value={searchTerm}
                      placeholder={i18n.t("label.organizationsearch")}
                      onChange={(event) => setSearchTerm(event.target.value)}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setSearchTerm("")}
                      disabled={!searchTerm}
                    >
                      {i18n.t("button.reset")}
                    </Button>
                  </InputGroup>
                </div>
                <div className="d-flex flex-wrap gap-3 mb-4">
                  <Badge bg="secondary">
                    {totalOrganizations} {i18n.t("label.organizations")}
                  </Badge>
                  <Badge bg="info">
                    {totalRoots} {i18n.t("label.rootorganizations")}
                  </Badge>
                  <Badge bg="success">
                    {totalLeaves} {i18n.t("label.leaforganizations")}
                  </Badge>
                  {hasSearch && (
                    <Badge bg="primary">
                      {visibleMatches} {i18n.t("label.searchresults")}
                    </Badge>
                  )}
                  {organizationsByLevel.map((count, index) => (
                    <Badge key={`level-${index}`} bg={index % 2 === 0 ? "dark" : "secondary"}>
                      {i18n.t("label.level")} {index + 1}: {count}
                    </Badge>
                  ))}
                </div>
                {loading ? (
                  <div className="displayFlexCenter py-5">
                    <Loader />
                  </div>
                ) : treeCount > 0 ? (
                  <OrganizationTreeView
                    filteredTree={filteredTree}
                    collapsedNodes={collapsedNodes}
                    normalizedSearchTerm={normalizedSearchTerm}
                    onToggleNode={toggleNode}
                    onGoToOrganizationDetails={goToOrganizationDetails}
                  />
                ) : (
                  <div>
                    {hasSearch
                      ? i18n.t("title.organizationstructurenoresults")
                      : i18n.t("title.organizationstructureempty")}
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    </JunoPage>
  );
};

export default OrganizationStructure;
